import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { PopupProps } from '@elfsight/widget-modal';
import { Popup } from '@elfsight/widget-modal';
import { scrollbarColoredMixin } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useActiveLocation } from '../active-location-context';
import { useLocationsFilter } from '../locations-filter-provider';
import { useAvailableFilters } from '../utils/use-available-filters';
import { DetailsFooter } from './details-footer';
import { LocationCard } from './location-card';

export type LocationCardPopupProps = Omit<
  PopupProps,
  'show' | 'onCloseRequest'
> & {
  mobileMode?: boolean;
};

export function LocationCardPopup({
  style,
  mobileMode = false,
  ...forwardingProps
}: LocationCardPopupProps) {
  const { backgroundColor, textColor } = useSettings();
  const { activeLocation, setActiveLocation } = useActiveLocation();

  const { availableFilters } = useAvailableFilters();
  const { currentFiltersStyle } = useLocationsFilter();
  const withInlineFilters =
    !!availableFilters.length && currentFiltersStyle === 'inline';

  return (
    <StyledPopup
      ariaLabel="Location card popup"
      show={!!activeLocation?.place && activeLocation._fulfilled}
      width={372}
      margin={24}
      withBackdrop={false}
      onCloseRequest={() => setActiveLocation(undefined)}
      style={{
        background: backgroundColor,
        ...style
      }}
      closeControlColor="#111"
      {...forwardingProps}
      _verticalOffset={withInlineFilters ? '100px' : '48px'}
      closeOnEscape
      withCloseControl
    >
      <ScrollableWrapper _textColor={textColor}>
        <LocationCard
          verticalPadding={mobileMode ? 20 : 40}
          horizontalPadding={mobileMode ? 20 : 40}
          footerComponent={<DetailsFooter showMap={mobileMode} />}
        />
      </ScrollableWrapper>
    </StyledPopup>
  );
}

const StyledPopup = styled(Popup)<{ _verticalOffset: string }>`
  border-radius: 12px;
  position: absolute;
  overflow: hidden;
  height: calc(100% - ${({ _verticalOffset }) => _verticalOffset});
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
`;

const ScrollableWrapper = styled.div<{ _textColor: string }>`
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  ${({ _textColor }) =>
    scrollbarColoredMixin(getColorWithAlpha(_textColor, 0.3))};
`;
